import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Layout, Menu, Breadcrumb, Button, Progress, Typography, Badge, Card, Spin, Space, message } from 'antd';
import { 
    HomeOutlined,
    BookOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    ArrowLeftOutlined,
    MessageOutlined
} from '@ant-design/icons';
import Chat from './Chat';
import '../App.css';
import { Col, Row, Select } from 'antd';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useMediaQuery } from 'react-responsive';
import ContextMenu from './ContextMenu';

const { Header, Content, Sider } = Layout;
const { Title, Text } = Typography;

const SearchInput = ({ courseID, placeholder, style, onSelect }) => {
    const [data, setData] = useState([]);
    const [value, setValue] = useState('');

    const fetch = (value, callback) => {
        if (!value) {
            callback([]);
            return;
        }

        axios.get(`https://api.galaxymentor.com/search`, {
            params: { courseID: courseID, query: value },
            headers: { 'Authorization': localStorage.getItem('token') }
        })
            .then(response => {
                const searchData = response.data.map(item => ({
                    value: item,
                    text: item
                }));
                callback(searchData);
            })
            .catch(error => {
                console.log('Search error:', error);
            });
    };

    const handleSearch = (newValue) => {
        fetch(newValue, setData);
    };

    const handleChange = (newValue) => {
        setValue(newValue);
        onSelect(newValue);
    };

    return (
        <Select
            showSearch
            value={value}
            placeholder={placeholder}
            style={style}
            defaultActiveFirstOption={false}
            filterOption={false}
            onSearch={handleSearch}
            onChange={handleChange}
            notFoundContent={null}
            options={(data || []).map(d => ({
                value: d.value,
                label: d.text,
            }))}
        />
    );
};

const CourseContent = () => {
    const { id } = useParams();
    const [menuItems, setMenuItems] = useState([]);
    const [selectedKey, setSelectedKey] = useState(null);
    const [collapsed, setCollapsed] = useState(false);
    const [courseTitle, setCourseTitle] = useState('');
    const [progress, setProgress] = useState(0);
    const [userPreferences, setUserPreferences] = useState({ language: 'English', depth: 'Medium' });
    
    // 使用useMemo来避免频繁的媒体查询检查
    const isSmallScreen = useMediaQuery({ maxWidth: 768 }, undefined, matches => matches);
    
    // 使用useMemo计算sider宽度，避免不必要的重新计算
    const { collapsedSiderWidth, siderWidth } = useMemo(() => {
        const collapsedWidth = isSmallScreen ? 55 : 80;
        const expandedWidth = collapsed ? collapsedWidth : 320;
        return {
            collapsedSiderWidth: collapsedWidth,
            siderWidth: expandedWidth
        };
    }, [isSmallScreen, collapsed]);
    
    const [contextMenuItems] = useState(['Clarify', 'Restate', 'Third Option']);
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [studyStartTime, setStudyStartTime] = useState(null);
    const navigate = useNavigate();

    // 使用useCallback来稳定函数引用
    const fetchUserPreferences = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            const response = await axios.get('http://localhost:8080/api/user/profile', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                setUserPreferences(response.data.data.preferences);
            }
        } catch (error) {
            console.error('Error fetching user preferences:', error);
            // 使用默认偏好
            setUserPreferences({
                language: 'English',
                depth: 'Medium'
            });
        }
    }, []);

    const fetchCourseContent = useCallback(async () => {
        try {
            setLoading(true);
            
            // 模拟课程数据，实际应该从API获取
            const mockCourse = {
                id: id,
                title: `Course ${id}`,
                description: 'This is a comprehensive course designed to help you master the fundamentals.',
                content: `
                    <h2>Welcome to Course ${id}</h2>
                    <p>This course will cover essential topics and provide hands-on experience.</p>
                    <h3>What you'll learn:</h3>
                    <ul>
                        <li>Core concepts and principles</li>
                        <li>Practical applications</li>
                        <li>Best practices and patterns</li>
                        <li>Real-world examples</li>
                    </ul>
                    <h3>Prerequisites:</h3>
                    <p>Basic understanding of programming concepts is recommended.</p>
                `,
                duration: '2-3 hours',
                difficulty: userPreferences?.depth || 'Medium',
                language: userPreferences?.language || 'English'
            };

            setCourse(mockCourse);
            
            // 创建或更新学习记录
            await createLearningRecord(mockCourse);
            
        } catch (error) {
            console.error('Error fetching course content:', error);
            message.error('Failed to load course content');
        } finally {
            setLoading(false);
        }
    }, [id, userPreferences]);

    const createLearningRecord = async (courseData) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            await axios.post('http://localhost:8080/api/user/learning-history', {
                course_id: courseData.id,
                course_name: courseData.title,
                course_description: courseData.description,
                status: 'in_progress',
                progress: 10, // 开始学习时设置为10%
                study_time: 0
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
        } catch (error) {
            console.error('Error creating learning record:', error);
            // 不显示错误消息，因为这不是关键功能
        }
    };

    const recordStudyTime = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token || !studyStartTime || !course) return;

            const studyDuration = Math.floor((Date.now() - studyStartTime) / 1000); // 秒
            
            if (studyDuration > 10) { // 只记录超过10秒的学习时间
                await axios.post('http://localhost:8080/api/user/learning-history', {
                    course_id: course.id,
                    course_name: course.title,
                    course_description: course.description,
                    status: 'in_progress',
                    progress: 50, // 可以根据实际进度调整
                    study_time: studyDuration
                }, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
            }
        } catch (error) {
            console.error('Error recording study time:', error);
        }
    }, [studyStartTime, course]);

    useEffect(() => {
        fetchUserPreferences();
        fetchCourseContent();
        setStudyStartTime(Date.now());
        
        // 组件卸载时记录学习时间
        return () => {
            recordStudyTime();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // 防抖处理响应式变化
    useEffect(() => {
        const timer = setTimeout(() => {
            setCollapsed(isSmallScreen);
        }, 100); // 100ms防抖

        return () => clearTimeout(timer);
    }, [isSmallScreen]);

    const findKeyBySubItemTitle = (subItemTitle) => {
        for (const chapter of menuItems) {
            for (const subItem of chapter.children) {
                if (subItem.label === subItemTitle) {
                    return subItem.key;
                }
            }
        }
        return null;
    };

    const handleSelectFromSearch = (subItemTitle) => {
        const key = findKeyBySubItemTitle(subItemTitle);
        if (key) {
            setSelectedKey(key);
            console.log(`Selected from search: ${key}`);
        }
    };

    const handleContextMenuClick = (menuItem) => {
        console.log(`Context menu item clicked: ${menuItem}`);
    };

    // 计算学习进度
    const calculateProgress = useCallback(() => {
        if (!selectedKey || menuItems.length === 0) return 0;
        
        let totalItems = 0;
        let currentIndex = 0;
        let found = false;
        
        menuItems.forEach(chapter => {
            chapter.children.forEach(subItem => {
                if (subItem.key === selectedKey && !found) {
                    currentIndex = totalItems;
                    found = true;
                }
                totalItems++;
            });
        });
        
        return totalItems > 0 ? Math.round(((currentIndex + 1) / totalItems) * 100) : 0;
    }, [selectedKey, menuItems]);

    useEffect(() => {
        const newProgress = calculateProgress();
        setProgress(newProgress);
    }, [calculateProgress]);

    useEffect(() => {
        const token = localStorage.getItem('token');

        axios.get(`https://api.galaxymentor.com/course_outline/${id}`, {
            headers: {
                'Authorization': token
            }
        })
            .then(function (response) {
                const courseOutline = response.data;
                console.log('Course outline data:', courseOutline); // 添加调试信息
                
                // 设置课程标题 - 尝试多种可能的字段名
                let actualCourseTitle = courseOutline.CourseTitle || 
                                      courseOutline.course_title || 
                                      courseOutline.title ||
                                      courseOutline.name;
                
                // 如果还是没有找到，尝试从课程列表API获取
                if (!actualCourseTitle) {
                    console.log('No course title found in outline, fetching from course list...');
                    fetchCourseTitle(id).then(title => {
                        if (title) {
                            setCourseTitle(title);
                        } else {
                            setCourseTitle(`Course ${id}`);
                        }
                    });
                } else {
                    setCourseTitle(actualCourseTitle);
                }

                if (courseOutline.Chapters && Array.isArray(courseOutline.Chapters)) {
                    const newMenuItems = courseOutline.Chapters.map((chapter, index) => ({
                        key: `ch${index + 1}`,
                        label: chapter.ChapterTitle || `Chapter ${index + 1}`,
                        icon: <BookOutlined />,
                        children: chapter.SubItems.map((subItem, subIndex) => ({
                            key: `${index + 1}.${subIndex + 1}`,
                            label: subItem.SubItemTitle,
                        }))
                    }));

                    setMenuItems(newMenuItems);
                    
                    // 自动选择第一个子项
                    if (newMenuItems.length > 0 && newMenuItems[0].children.length > 0) {
                        setSelectedKey(newMenuItems[0].children[0].key);
                    }
                }
            })
            .catch(function (error) {
                alert("To view this course, please login");
                window.location.href = "/login";
            });
    }, [id]);

    // 辅助函数：从课程列表API获取课程标题
    const fetchCourseTitle = async (courseId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`https://api.galaxymentor.com/all-courses`, {
                method: 'GET',
                headers: {
                    'Authorization': token,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                const courses = await response.json();
                const course = courses.find(c => c.courseid.toString() === courseId.toString());
                console.log('Found course in list:', course);
                return course?.course_title || course?.title || course?.name;
            }
        } catch (error) {
            console.error('Error fetching course title:', error);
        }
        return null;
    };

    const onClick = ({ key }) => {
        setSelectedKey(key);
        console.log(`Clicked: ${key}`);
    };

    const getCurrentChapterAndLesson = () => {
        if (!selectedKey) return { chapter: '', lesson: '' };
        
        for (const chapter of menuItems) {
            for (const subItem of chapter.children) {
                if (subItem.key === selectedKey) {
                    return {
                        chapter: chapter.label,
                        lesson: subItem.label
                    };
                }
            }
        }
        return { chapter: '', lesson: '' };
    };

    const { chapter, lesson } = getCurrentChapterAndLesson();

    if (loading) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '400px' 
            }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!course) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Text type="secondary">Course not found</Text>
            </div>
        );
    }

    return (
        <Layout style={{ minHeight: 'calc(100vh - 64px)' }} className="layout-stable">
            <Sider 
                width={siderWidth}
                className="resize-observer-safe"
                style={{
                    overflow: 'auto',
                    height: 'calc(100vh - 64px)',
                    position: 'fixed',
                    left: 0,
                    top: 64,
                    bottom: 0,
                    background: '#001529',
                    boxShadow: '2px 0 8px rgba(0,0,0,0.15)'
                }} 
                collapsible 
                collapsed={collapsed} 
                onCollapse={setCollapsed}
                trigger={
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '12px',
                        background: 'rgba(255,255,255,0.1)',
                        borderTop: '1px solid rgba(255,255,255,0.1)'
                    }}>
                        {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                    </div>
                }
            >
                {/* 课程标题区域 */}
                <div className="stable-layout" style={{ 
                    padding: collapsed ? '16px 8px' : '20px 16px', 
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    textAlign: 'center'
                }}>
                    {!collapsed && (
                        <>
                            <Title level={4} style={{ color: 'white', margin: 0, marginBottom: 8 }}>
                                {courseTitle}
                            </Title>
                            <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
                                Course {id}
                            </Text>
                        </>
                    )}
                </div>

                {/* 进度条 */}
                {!collapsed && (
                    <div className="stable-layout" style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                        <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, display: 'block', marginBottom: 8 }}>
                            Learning Progress
                        </Text>
                        <Progress 
                            percent={progress} 
                            size="small" 
                            strokeColor="#52c41a"
                            trailColor="rgba(255,255,255,0.1)"
                        />
                    </div>
                )}

                {/* 搜索框 */}
                {!collapsed && (
                    <div className="stable-layout" style={{ padding: '16px' }}>
                        <SearchInput 
                            courseID={id} 
                            placeholder="Search topics..." 
                            style={{ width: '100%' }} 
                            onSelect={handleSelectFromSearch} 
                        />
                    </div>
                )}

                {/* 菜单 */}
                <Menu 
                    theme="dark" 
                    mode="inline" 
                    selectedKeys={[selectedKey]} 
                    items={menuItems} 
                    onClick={onClick}
                    className="stable-layout"
                    style={{ 
                        border: 'none',
                        background: 'transparent'
                    }}
                />
            </Sider>

            <Layout className="responsive-container" style={{ marginLeft: siderWidth }}>
                {/* 顶部导航栏 */}
                <Header className="stable-layout" style={{
                    background: 'white',
                    padding: '0 24px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 100
                }}>
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Breadcrumb style={{ margin: '16px 0' }}>
                                <Breadcrumb.Item>
                                    <Link to="/course-selection">
                                        <HomeOutlined /> Courses
                                    </Link>
                                </Breadcrumb.Item>
                                <Breadcrumb.Item>
                                    <BookOutlined /> {courseTitle}
                                </Breadcrumb.Item>
                                {chapter && (
                                    <Breadcrumb.Item>{chapter}</Breadcrumb.Item>
                                )}
                                {lesson && (
                                    <Breadcrumb.Item>{lesson}</Breadcrumb.Item>
                                )}
                            </Breadcrumb>
                        </Col>
                        <Col>
                            <Badge count={progress + '%'} style={{ backgroundColor: '#52c41a' }}>
                                <Button type="text" icon={<BookOutlined />} />
                            </Badge>
                        </Col>
                    </Row>
                </Header>

                <Content className="chat-layout-container" style={{ 
                    margin: 0, 
                    background: '#f5f5f5',
                    minHeight: 'calc(100vh - 128px)'
                }}>
                    <div style={{ padding: '24px', height: '100%' }}>
                        <Chat
                            selectedKey={selectedKey}
                            setSelectedKey={setSelectedKey}
                            course_id={id}
                            userPreferences={userPreferences}
                        />
                    </div>
                </Content>

            </Layout>
            
            <ContextMenu menuItems={contextMenuItems} onMenuClick={handleContextMenuClick} />
        </Layout>
    );
};

export default CourseContent;
